/**
 * The three package tenants of `extras.poqpoq.*` — ADR-033 phase 2.
 *
 * World's "direct pass" (comms 2026-09-04): everything the avatar needs to
 * be worn lives in the GLB, so a downloaded file uploads through their picker
 * and loses nothing. Three declarations ride the ROOT node, all plain JSON
 * with no buffer or bufferView index (their ADR-195 lift preserves `extras`
 * byte-for-byte and audits nothing inside it):
 *
 *   height     { metres, provenance }   — World READS this today (PR #1626):
 *                                         any node, root first, `meters` too,
 *                                         ignored outside 0.3–6 m
 *   package    <the roster record>      — what ingest merges into the roster
 *   thumbnail  data:image/png;base64,…  — the starred hero take, ≤ 512², for
 *                                         the carousel out of the container
 *
 * Same door as head seating and arm-space (`poqpoq-extras.ts`): three carries
 * `userData` ↔ `extras` both ways, so the scene writer sets userData on the
 * root and the export door writes bytes. Readers refuse malformed markers by
 * name; an ABSENT marker is simply absent.
 *
 * Depends on: zod, ir/errors, poqpoq-extras, package. Depended on by: the
 * adapter's scene writer, the reopen census, the package door.
 */
import { z } from 'zod';
import { KernelError } from '../ir/errors.js';
import { readPoqpoqExtra, withPoqpoqExtra } from './poqpoq-extras.js';
import { HEIGHT_METERS_BAND, packageRecordSchema, parsePackageRecord, type PackageRecord } from './package.js';

export const HEIGHT_MARKER_KEY = 'height';
export const PACKAGE_MARKER_KEY = 'package';
export const THUMBNAIL_MARKER_KEY = 'thumbnail';

export class PackageExtrasRefusal extends KernelError {}

export const heightMarkerSchema = z
  .object({
    metres: z.number().optional(),
    /** World accepts the US spelling too; we WRITE `metres` and READ both. */
    meters: z.number().optional(),
    provenance: z.enum(['explicit', 'default']),
  })
  .superRefine((m, ctx) => {
    const v = m.metres ?? m.meters;
    if (v === undefined) ctx.addIssue({ code: 'custom', message: 'neither metres nor meters' });
    else if (!(v > HEIGHT_METERS_BAND.min && v <= HEIGHT_METERS_BAND.max)) {
      ctx.addIssue({ code: 'custom', message: `${v} m is outside (${HEIGHT_METERS_BAND.min}, ${HEIGHT_METERS_BAND.max}]` });
    }
  });

export interface HeightMarker {
  readonly metres: number;
  readonly provenance: 'explicit' | 'default';
}

/**
 * Validate on the way IN, like the record and thumbnail writers: a
 * TypeScript-valid `{ metres: 9 }` would otherwise write a marker this
 * module's own reader refuses and World ignores (Codex P2, #148).
 */
export function withHeightMarker(userData: unknown, marker: HeightMarker | null): Record<string, unknown> {
  if (marker === null) return withPoqpoqExtra(userData, HEIGHT_MARKER_KEY, undefined);
  const parsed = heightMarkerSchema.safeParse({ metres: marker.metres, provenance: marker.provenance });
  if (!parsed.success) {
    throw new PackageExtrasRefusal(`height marker refused: ${parsed.error.issues.map((i) => i.message).join('; ')}`);
  }
  return withPoqpoqExtra(userData, HEIGHT_MARKER_KEY, { metres: marker.metres, provenance: marker.provenance });
}

export function parseHeightMarker(userData: unknown, where: string): HeightMarker | null {
  const raw = readPoqpoqExtra(userData, HEIGHT_MARKER_KEY);
  if (raw === undefined) return null;
  const parsed = heightMarkerSchema.safeParse(raw);
  if (!parsed.success) {
    throw new PackageExtrasRefusal(
      `${where} carries a malformed height marker (extras.poqpoq.${HEIGHT_MARKER_KEY}): ${parsed.error.issues.map((i) => i.message).join('; ')}`,
    );
  }
  return { metres: (parsed.data.metres ?? parsed.data.meters) as number, provenance: parsed.data.provenance };
}

export function withPackageMarker(userData: unknown, record: PackageRecord | null): Record<string, unknown> {
  // Validate on the way IN: a file must never carry a record ingest would refuse.
  return withPoqpoqExtra(userData, PACKAGE_MARKER_KEY, record === null ? undefined : packageRecordSchema.parse(record));
}

export function parsePackageMarker(userData: unknown, where: string): PackageRecord | null {
  const raw = readPoqpoqExtra(userData, PACKAGE_MARKER_KEY);
  if (raw === undefined) return null;
  try {
    return parsePackageRecord(raw);
  } catch (e) {
    throw new PackageExtrasRefusal(`${where} carries a malformed package record (extras.poqpoq.${PACKAGE_MARKER_KEY}): ${e instanceof Error ? e.message : String(e)}`);
  }
}

/** World sized the card at 512² ≈ 100 KB; twice that is the ceiling before the container gets heavy. */
export const THUMBNAIL_MAX_BYTES = 200_000;
const PNG_DATA_URI = /^data:image\/png;base64,([A-Za-z0-9+/]+={0,2})$/;

/** Bytes of a PNG data URI's payload, without decoding it. */
export function thumbnailBytes(dataUri: string): number {
  const m = PNG_DATA_URI.exec(dataUri);
  if (m === null) return -1;
  const b64 = m[1]!;
  return Math.floor((b64.length * 3) / 4) - (b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0);
}

/** The one check both doors run: a PNG data URI under the ceiling, or the reason it is not. */
function thumbnailProblem(raw: unknown): string | null {
  const bytes = typeof raw === 'string' ? thumbnailBytes(raw) : -1;
  if (bytes < 0) return 'not a PNG data URI (data:image/png;base64,…)';
  if (bytes > THUMBNAIL_MAX_BYTES) return `${bytes} bytes — over the ${THUMBNAIL_MAX_BYTES}-byte ceiling; capture at 512² or smaller`;
  return null;
}

export function withThumbnailMarker(userData: unknown, dataUri: string | null): Record<string, unknown> {
  if (dataUri !== null) {
    const problem = thumbnailProblem(dataUri);
    if (problem !== null) throw new PackageExtrasRefusal(`thumbnail refused: ${problem}`);
  }
  return withPoqpoqExtra(userData, THUMBNAIL_MARKER_KEY, dataUri === null ? undefined : dataUri);
}

/**
 * The read path enforces the SAME ceiling as the write path (Codex P2, #146):
 * a file this door never wrote could carry a multi-megabyte data URI, and the
 * census would hand it on into JSON and memory as if it were ours.
 */
export function parseThumbnailMarker(userData: unknown, where: string): string | null {
  const raw = readPoqpoqExtra(userData, THUMBNAIL_MARKER_KEY);
  if (raw === undefined) return null;
  const problem = thumbnailProblem(raw);
  if (problem !== null) {
    throw new PackageExtrasRefusal(`${where} carries a malformed thumbnail (extras.poqpoq.${THUMBNAIL_MARKER_KEY}): ${problem}`);
  }
  return raw as string;
}
